import { useEffect, useState } from 'react';
import { config, isDevelopment } from '../config';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export function useRecaptcha() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Skip reCAPTCHA in development if no site key is provided
    if (isDevelopment && !config.recaptchaSiteKey) {
      setIsLoaded(true);
      return;
    }

    if (!config.recaptchaSiteKey) {
      console.error('reCAPTCHA site key not configured');
      return;
    }

    // Load reCAPTCHA script if not already loaded
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${config.recaptchaSiteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.grecaptcha.ready(() => {
          setIsLoaded(true);
        });
      };
      script.onerror = () => {
        console.error('Failed to load reCAPTCHA script');
      };
      document.head.appendChild(script);
    } else {
      window.grecaptcha.ready(() => {
        setIsLoaded(true);
      });
    }
  }, []);

  const executeRecaptcha = async (action: string): Promise<string | null> => {
    // In development, return a dummy token if no site key is configured
    if (isDevelopment && !config.recaptchaSiteKey) {
      return 'dev-token';
    }

    if (!isLoaded || !window.grecaptcha || !config.recaptchaSiteKey) {
      throw new Error('reCAPTCHA not loaded or configured');
    }

    try {
      const token = await window.grecaptcha.execute(config.recaptchaSiteKey, {
        action,
      });
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  return {
    isLoaded,
    executeRecaptcha,
  };
}
